/* eslint-disable camelcase */
import styles from './AttributionStages.module.scss'

import type { AttributionDiagnostics } from '@/pages/app/AttributionViewer/attributionMethods'

const STAGE_NAMES: Record<string, string> = {
  evidence: 'Evidence',
  room: 'Room',
  cluster: 'Clustering',
  assign: 'Assignment',
  gap_fill: 'Gap fill',
  classical_rescue: 'Classical rescue',
  activate: 'Activation',
}

const readable = (value: string) => value.replaceAll('_', ' ')

export default function AttributionStages({
  diagnostics,
  isLoading,
  hasError,
}: {
  diagnostics: AttributionDiagnostics | null
  isLoading: boolean
  hasError: boolean
}) {
  const message = isLoading
    ? 'Loading recorded substeps…'
    : hasError
      ? 'Recorded substeps could not be loaded. Refresh artifacts to retry.'
      : diagnostics?.reason === 'revision_mismatch'
        ? 'The transcript revision changed. Refresh artifacts to load matching substeps.'
        : diagnostics?.reason === 'invalid_artifact'
          ? 'Recorded substeps could not be read.'
          : 'Substeps were not recorded for this run.'
  const stages = !isLoading && !hasError && diagnostics?.status === 'available' ? diagnostics.stages : []
  return (
    <details className={styles.panel}>
      <summary>
        Attribution substeps
        {stages.length > 0 ? ` · ${stages.length} recorded` : ''}
      </summary>
      <p>Recorded during transcript processing. Durations apply to the whole run, not individual sections.</p>
      {stages.length === 0 ? (
        <p role="status">{message}</p>
      ) : (
        <ol className={styles.stages}>
          {stages.map((stage, index) => (
            <li key={`${stage.stage}-${index}`}>
              <div className={styles.heading}>
                <strong>{STAGE_NAMES[stage.stage] ?? readable(stage.stage)}</strong>
                <span>{readable(stage.outcome)}</span>
                <span className={styles.duration}>{stage.duration_ms.toLocaleString()} ms</span>
              </div>
              {stage.variant && <span>Method: {readable(stage.variant)}</span>}
              {stage.fallback_from && <span>Fallback from: {readable(stage.fallback_from)}</span>}
              {stage.reason && <span>Reason: {readable(stage.reason)}</span>}
            </li>
          ))}
        </ol>
      )}
    </details>
  )
}
